import type {RiskFactor, RiskScore} from '../types/categoryProfileTypes';
import {LOCALE} from './locale';

const CONFIDENCE: Record<RiskScore['confidence'], string> = {
    full: 'Full data', partial: 'Partial data', limited: 'Limited data',
};

/** "92nd", "41st", "3rd". */
export function ordinal(n: number): string {
    const r = Math.round(n);
    const teen = r % 100 >= 11 && r % 100 <= 13;
    const suffix = teen ? 'th' : ({1: 'st', 2: 'nd', 3: 'rd'} as Record<number, string>)[r % 10] ?? 'th';
    return `${r}${suffix}`;
}

/** The raw values behind a factor, in words (e.g. "top supplier 71%, 4 suppliers"). */
export function factorValues(f: RiskFactor): string {
    const v = f.values ?? {};
    const parts: string[] = [];
    if (f.key === 'concentration') {
        if (v.topSharePct != null) parts.push(`top supplier ${v.topSharePct}%`);
        if (v.suppliers != null) parts.push(`${v.suppliers} supplier${v.suppliers === 1 ? '' : 's'}`);
    } else if (f.key === 'coverage') {
        if (v.poSharePct != null) parts.push(`${v.poSharePct}% on a PO`);
        if (v.contractSharePct != null) parts.push(`${v.contractSharePct}% under contract`);
    } else if (f.key === 'volatility') {
        if (v.monthlyVariationPct != null) parts.push(`monthly variation ${v.monthlyVariationPct}%`);
        if (v.growthPct != null) parts.push(`${v.growthPct >= 0 ? '+' : ''}${v.growthPct}% on the year before`);
    } else if (v.score != null) {
        parts.push(`external score ${v.score}`);
    }
    return parts.join(', ');
}

/** "Set to Medium by Arash on 1 Nov 2026: framework agreement (calculated: High)"; null without a level by the team. */
export function overrideText(score: RiskScore): string | null {
    const o = score.override;
    if (!o) return null;
    const when = new Date(o.setAt).toLocaleDateString(LOCALE, {day: 'numeric', month: 'short', year: 'numeric'});
    const who = o.setBy ? ` by ${o.setBy}` : '';
    if (!o.level) return `Factors left out${who} on ${when}: ${o.reason}`;
    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    const calculated = score.calculatedLevel === 'insufficient' ? 'too little data' : cap(score.calculatedLevel ?? '—');
    return `Set to ${cap(o.level)}${who} on ${when}: ${o.reason} (calculated: ${calculated})`;
}

/** Lower-case only the first letter, so abbreviations such as PO stay as they are. */
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export const confidenceLabel = (c: RiskScore['confidence']) => CONFIDENCE[c];

/** One line per point: why the category has its level, which factors counted and which did not (tooltip). */
export function riskExplanation(score: RiskScore): string[] {
    const team = overrideText(score);
    if (score.level === 'insufficient') return [score.reason ?? 'Not ranked: too little data.'];
    if (score.calculatedLevel === 'insufficient') return [...(team ? [team] : []), score.reason ?? 'Not ranked: too little data.'];
    const lines = [...(team ? [team] : []),
        `Supply risk ${ordinal(score.supplyRisk ?? 0)} percentile, spend ${ordinal(score.impact ?? 0)} percentile `
        + `(against the other categories at this level).`,
    ];
    for (const f of score.factors) {
        if (f.used) {
            const values = factorValues(f);
            lines.push(`${f.label}: ${ordinal(f.percentile ?? 0)} percentile${values ? ` (${values})` : ''}, ${f.weightPct}% of the score`);
        }
    }
    const unused = score.factors.filter(f => !f.used);
    if (unused.length) lines.push(`Not used: ${unused.map(f => `${f.label.toLowerCase()} (${lowerFirst(f.reason ?? '')})`).join('; ')}`);
    lines.push(`${confidenceLabel(score.confidence)}: based on ${score.weightUsedPct}% of the configured weight.`);
    return lines;
}
